import * as React from 'react';
import { getMaybeActiveUserOrRefresh, redirectToErrorMessage } from '../../api/serverApiHelpers';
import { SubscriberArticleSort, ArticleType, SimpleArticleList, SubscriberArticlePage, SubscriberArticleListing } from '../../amorg-common';
import { getArticleTypes, getArticleListTopics, getArticleListing, downloadArticlesLight } from '../../api/siteArticlesClientApi';
import { ImportanceAvatar } from '../../designSystem/avatar/ImportanceAvatar';
import { Button } from '../../designSystem/button/Button';
import { LinkButton } from '../../designSystem/button/LinkButton';
import { colors } from '../../designSystem/colors/color';
import { CardPager } from '../../designSystem/compositeComponents/CardPager';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { FormField } from '../../designSystem/formField/FormField';
import { SelectField } from '../../designSystem/formField/SelectField';
import { TextInput } from '../../designSystem/inputs/TextInput';
import { Label } from '../../designSystem/Label';
import { ProtectedPageWrapper } from '../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { DarkLink } from '../../designSystem/Link';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { Table, TableHeaderIcon, TableHeaderIconWithSort, TableHeaderWithSort } from '../../designSystem/Table';
import { amorgLocalStorage, ClientArticlePageFilter, getDefaultClientArticlePageFilter } from '../../utils/amorgLocalStorage';
import { toDateString } from '../../utils/date';

export function ArticlesLightPage (): JSX.Element
{
    const [loading, setLoading] = React.useState(true);

    const [types, setTypes] = React.useState<ArticleType[]>([]);
    const [listTopics, setListTopics] = React.useState<SimpleArticleList[]>([]);
    
    const [listId, setListId] = React.useState<string>('');
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [filter, setFilter] = React.useState<ClientArticlePageFilter>(getDefaultClientArticlePageFilter());    

    const [listing, setListing] = React.useState<SubscriberArticlePage | null>(null);

    React.useEffect(() =>
    {
        const listId = amorgLocalStorage.articles.articlesLight.listId.read(amorgLocalStorage.articles.articlesLight.listId.getKey);
        setListId(listId); 
        getData(listId, '');

    }, []);

    async function getData(newListId:string, newSearchTerm:string)
    {        
        // Force refresh if needed to prevent empty lists triggering "no access" error on users with expired tokens
        await getMaybeActiveUserOrRefresh();

        setLoading(true);

        const listTopics = await getArticleListTopics();
        if(listTopics.length === 0)
            redirectToErrorMessage('Der opstod en fejl ved indlæsning af lovkravsemner. Prøv at genindlæse siden, og kontakt support hvis problemet fortsætter.');
        setListTopics(listTopics);

        const types = await getArticleTypes();
        setTypes(types);

        const newFilter = amorgLocalStorage.articles.readFilter(
            newListId,
            listTopics,
            types,
            []
        );

        setFilter(newFilter);

        const listing = await getArticleListing(newListId, newFilter.topicId, newFilter.typeId, newFilter.ownerUserId, newFilter.page, newSearchTerm, newFilter.sort);
        setListing(listing);
        setLoading(false);
    }

    async function applyFilter(newListId:string, newSearchTerm:string, filter: ClientArticlePageFilter)
    {
        setLoading(true);
        setListId(newListId);
        setSearchTerm(newSearchTerm);
        setFilter(filter);
        
        const newListing = await getArticleListing(newListId, filter.topicId, filter.typeId, filter.ownerUserId, filter.page, newSearchTerm, filter.sort);
        amorgLocalStorage.articles.saveFilter(newListId, filter);
        setListing(newListing);
        setLoading(false);
    }

    async function handleSelectListId (newListId: string)
    {
        setListId(newListId);
        amorgLocalStorage.articles.articlesLight.listId.write(amorgLocalStorage.articles.articlesLight.listId.getKey, newListId);
        applyFilter(newListId, searchTerm, { ...filter, topicId:'', page: 1 });
    }

    async function handleSelectType (typeId: string)
    {
        applyFilter(listId, searchTerm, { ...filter, typeId, page: 1 });
    }

    async function handleSortChange (sortBy: SubscriberArticleSort, ascending: boolean)
    {
        applyFilter(listId, searchTerm, { ...filter, sort: { sortBy, ascending } });
    }

    async function handlePageChange (newPage: number)
    {
        applyFilter(listId, searchTerm, { ...filter, page: newPage });
    }

    return (

        <ProtectedPageWrapper requiredPermissions={['ViewCustomFormAnswerList']} selectedMenuItemKey={'articleslight'}>
            <SingleColumnLayout
                wide
                noDivider
                contentContainer={
                    <Flex flexDirection='column' gap={32}>

                        <LoadingPageOverlay loading={loading} />

                        <PageHeader 
                            headerStyle="secondary-gray"
                            headerIcon='gavel'
                            headerText='Vidensdatabasen'
                            buttons={
                                <div style={{marginTop:'32px'}}>
                                    <LinkButton variant="outlined" href={downloadArticlesLight} icon='interface_actions_download' tooltipContent={<div>Download artikler som Excel</div>} />
                                </div>
                            }
                        />

                        <Flex flexDirection="column" gap={16}>
                            <Flex gap={32}>

                                <div style={{flexGrow: 1}}>
                                <SelectField
                                    label='Liste'
                                    minWidth="200px"
                                    emptyText='Alle lister'
                                    options={listTopics.map(l => ({ value: l.id, text: l.name }))}
                                    selectedValue={listId}
                                    onChange={handleSelectListId}
                                />
                                </div>

                                <div style={{flexGrow: 1}}>
                                <SelectField
                                    label='Type'
                                    emptyText='Alle typer'
                                    options={types.map(t => ({ value: t.id, text: t.name }))}
                                    selectedValue={filter.typeId}
                                    onChange={handleSelectType}
                                />
                                </div>
                                
                            </Flex>
                            <Flex justifyContent='space-between'>
                                <FormField
                                    label='Søgetekst'
                                    inputComponent={
                                        <Flex gap={4} alignItems='center' style={{width:'400px'}}>
                                            <div style={{flexGrow: 1}}>
                                            <TextInput trailingIcon='interface_actions_search'
                                                value={searchTerm}
                                                onChange={(value) => setSearchTerm(value)}
                                                onKeyDown={(e) =>
                                                {
                                                    if (e.key === 'Enter')
                                                        applyFilter(listId, searchTerm, filter);
                                                }} />
                                            </div>

                                            <Button variant='text' icon='actions_close' toolTipText='Ryd søgetekst' onClick={() => { 
                                                applyFilter(listId, '', { ...filter });
                                            }} />
                                        </Flex>
                                    }
                                />
                            </Flex>


                            <section>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <TableHeaderIconWithSort
                                                icon='calendar'
                                                width='100px'
                                                tooltipContent='Dato for seneste ændring'
                                                isActiveSort={filter.sort.sortBy === 'releaseDate'}
                                                ascending={filter.sort.ascending}
                                                onClick={(ascending) => handleSortChange('releaseDate', ascending)}
                                            />
                                            <TableHeaderIcon 
                                                icon='gavel'
                                                width='100px'
                                                tooltipContent='Dato loven er gældende fra' 
                                            />
                                            <TableHeaderWithSort
                                                text="Titel"
                                                isActiveSort={filter.sort.sortBy === 'title'}
                                                ascending={filter.sort.ascending}
                                                onClick={(ascending) => handleSortChange('title', ascending)}
                                            />
                                            <TableHeaderWithSort
                                                text="Liste"
                                                isActiveSort={filter.sort.sortBy === 'list'}
                                                ascending={filter.sort.ascending}
                                                onClick={(ascending) => handleSortChange('list', ascending)}
                                            />
                                            <TableHeaderWithSort
                                                text="Type"
                                                isActiveSort={filter.sort.sortBy === 'type'}
                                                ascending={filter.sort.ascending}
                                                onClick={(ascending) => handleSortChange('type', ascending)}
                                            />
                                            <th>
                                                Nummer
                                            </th>

                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {listing && listing.articles.map(article => 
                                            <SummaryRow key={article.articleId} article={article} />
                                        )}
                                    </tbody>
                                </Table>

                                <CardPager
                                    currentPage={listing?.page || 1}
                                    items={listing?.totalItems || 0}
                                    itemsPerPage={listing?.itemsPerPage || 20}
                                    onPageChange={handlePageChange}
                                />

                            </section>

                        </Flex>
                    </Flex>
                } />

        </ProtectedPageWrapper>
    );
}

type SummaryRowProps = {
    article: SubscriberArticleListing
}
function SummaryRow(props:SummaryRowProps):JSX.Element
{
    const [expanded, setExpanded] = React.useState(false);

    function getTd(content:React.ReactNode):JSX.Element
    {
        return (
            <td style={{ fontSize:'14px', borderBottom: expanded ? 'none' : `1px solid ${colors.table.borderInternal}` }}>
                {content}
            </td>
        );
    }

    return (
        <>
            <tr>
                {getTd(<ImportanceAvatar importance={props.article.publication.importance} size='16px' />)}
                
                {getTd(props.article.release ? toDateString(props.article.release.releasedDate) : '')}

                {getTd(props.article.date ? toDateString(props.article.date) : '')}
                
                {getTd(
                    props.article.url 
                        ?   <DarkLink href={props.article.url} target='_blank' rel='noreferrer'>
                                {props.article.title}
                            </DarkLink>
                        :   props.article.title
                )}
                
                {getTd(props.article.list.name)}
                
                {getTd(props.article.type.name)}

                {getTd(props.article.reference)}

                {getTd(
                    <Button
                        variant='text'
                        icon={expanded ? 'caret_caretUp' : 'caret_caretDown'}
                        toolTipText={expanded ? 'Skjul detaljer' : 'Vis detaljer'}
                        onClick={() => setExpanded(!expanded)}
                    />
                )}
            </tr>
            {expanded &&
                <tr>
                    <td colSpan={6}>
                        <Flex flexDirection="column" style={{padding:'0px 20px'}}>
                            
                            <div>
                                <Label>Beskrivelse</Label>
                                <div style={{fontSize:'14px'}} dangerouslySetInnerHTML={{ __html: props.article.description || '' }} />
                            </div>

                            {props.article.publication.changeDescription &&
                                <div>
                                    <Label>Ændringsbeskrivelse</Label>
                                    <div style={{fontSize:'14px'}} dangerouslySetInnerHTML={{ __html: props.article.publication.changeDescription || '' }} />
                                </div>
                            }

                            {props.article.publication.assessment &&

                                <div>
                                    <Label>Vurdering</Label>
                                    <div style={{fontSize:'14px'}} dangerouslySetInnerHTML={{ __html: props.article.publication.assessment || '' }} />
                                </div>
                            }

                        </Flex>
                    </td>
                </tr>
            }
        </>
    );
}